import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchBrand, fetchBrands } from "@/lib/api";
import { useI18n, pickLocalized } from "@/lib/i18n";
import StatusChip from "@/components/StatusChip";
import Seo from "@/components/Seo";
import { X, Plus } from "lucide-react";

const CATS = ["PRODUCT_IDENTITY","MATERIALS","ORIGIN","REPAIR_CARE","END_OF_LIFE","FOOTPRINT","CERTIFICATIONS"];

export default function Compare() {
  const [params, setParams] = useSearchParams();
  const { t, locale } = useI18n();
  const slugs = useMemo(() => (params.get("brands") || "").split(",").filter(Boolean), [params]);
  const [data, setData] = useState({});
  const [all, setAll] = useState([]);

  useEffect(() => {
    fetchBrands({ limit: 200 }).then((d) => setAll(d.brands || []));
  }, []);

  useEffect(() => {
    slugs.forEach((s) => {
      if (!data[s]) fetchBrand(s).then((d) => setData((prev) => ({ ...prev, [s]: d }))).catch(() => { /* ignore */ });
    });
  }, [slugs, data]);

  const setSlugs = (newSlugs) => {
    const next = new URLSearchParams(params);
    if (newSlugs.length) next.set("brands", newSlugs.join(","));
    else next.delete("brands");
    setParams(next);
  };

  const remove = (s) => setSlugs(slugs.filter((x) => x !== s));
  const add = (s) => { if (slugs.length < 3 && !slugs.includes(s)) setSlugs([...slugs, s]); };

  const brands = useMemo(() => slugs.map((s) => data[s]).filter(Boolean), [slugs, data]);
  const title = brands.length ? brands.map((b) => b.brand.display_name).join(" vs ") : t.compare_title;

  return (
    <>
      <Seo title={title} description={t.compare_intro} canonicalPath={`/compare?brands=${slugs.join(",")}`} locale={locale} />
      <div className="bp-container py-10">
        <h1 className="font-serif text-4xl md:text-5xl mb-3">{t.compare_title}</h1>
        <p className="max-w-2xl mb-8" style={{ color: "var(--bp-text-secondary)" }}>{t.compare_intro}</p>

        <div className="grid gap-4" style={{ gridTemplateColumns: `240px repeat(${Math.max(brands.length, 1)}, minmax(0, 1fr)) auto` }}>
          <div />
          {brands.map((b) => (
            <div key={b.brand.id} className="p-4 bg-white border" style={{ borderColor: "var(--bp-border)" }} data-testid={`compare-col-${b.brand.slug}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>{b.brand.sector_code}</div>
                  <div className="font-serif text-2xl">{b.brand.display_name}</div>
                </div>
                <button onClick={() => remove(b.brand.slug)} data-testid={`compare-remove-${b.brand.slug}`}><X size={16} /></button>
              </div>
              <div className="mt-2 text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>{t.publishes_of_seven(b.profile?.published_count ?? 0)}</div>
            </div>
          ))}
          {brands.length < 3 && (
            <div className="p-4 bg-white border border-dashed" style={{ borderColor: "var(--bp-border)" }}>
              <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--bp-text-muted)" }}>{t.compare_pick}</div>
              <select
                data-testid="compare-add-select"
                onChange={(e) => { if (e.target.value) { add(e.target.value); e.target.value = ""; } }}
                className="w-full border px-2 py-1.5 text-sm bg-white"
                style={{ borderColor: "var(--bp-border)" }}
                defaultValue=""
              >
                <option value="">— select —</option>
                {all.filter((a) => !slugs.includes(a.slug)).map((a) => (
                  <option key={a.slug} value={a.slug}>{a.display_name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {brands.length > 0 && (
          <div className="mt-10 border" style={{ borderColor: "var(--bp-border)", background: "#fff" }}>
            {CATS.map((cat) => (
              <div key={cat} className="grid border-t first:border-t-0" style={{ gridTemplateColumns: `240px repeat(${brands.length}, minmax(0, 1fr))`, borderColor: "var(--bp-border)" }}>
                <div className="p-4 font-serif text-base border-r" style={{ borderColor: "var(--bp-border)" }}>{t.checklist_categories[cat]}</div>
                {brands.map((b) => {
                  const item = b.profile?.checklist?.find((c) => c.category === cat);
                  return (
                    <div key={b.brand.id} className="p-4 border-r last:border-r-0" style={{ borderColor: "var(--bp-border)" }}>
                      {item ? <StatusChip status={item.status} testId={`compare-${b.brand.slug}-${cat}`} /> : "—"}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {brands.length > 0 && (
          <div className="mt-10 grid gap-6" style={{ gridTemplateColumns: `repeat(${brands.length}, minmax(0, 1fr))` }}>
            {brands.map((b) => (
              <div key={b.brand.id}>
                <div className="uppercase text-xs tracking-[0.3em] mb-2" style={{ color: "var(--bp-text-muted)" }}>{b.brand.display_name}</div>
                <p className="text-sm leading-relaxed" style={{ color: "var(--bp-text-secondary)" }}>{pickLocalized(b.profile?.summary, locale)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
